import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NATIVE = ['de', 'en', 'fr', 'it'];
const SKIP = new Set(['SCRIPT', 'STYLE', 'INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'CODE', 'NOSCRIPT', 'IFRAME']);
const HAS_LETTERS = /[A-Za-z\u00C0-\u024F]{2,}/;
const originals = new WeakMap();
const memo = {};

const collect = (root) => {
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      const p = n.parentElement;
      if (!p || SKIP.has(p.tagName) || p.closest('[data-no-translate]')) return NodeFilter.FILTER_REJECT;
      const src = originals.get(n) ?? n.nodeValue;
      return HAS_LETTERS.test(src) && src.trim().length > 1 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
};

// DOM-level auto-translation for the 16 languages without native UI dictionaries.
export const PageTranslator = () => {
  const { contentLang } = useApp();
  const { pathname } = useLocation();
  const busy = useRef(false);
  const prev = useRef(contentLang);

  useEffect(() => {
    const lang = contentLang;
    const root = document.getElementById('root');
    if (!root) return undefined;
    const wasTranslated = !NATIVE.includes(prev.current);
    prev.current = lang;
    if (NATIVE.includes(lang)) {
      if (wasTranslated) window.location.reload();
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const nodes = collect(root);
        const pending = [];
        nodes.forEach((n) => {
          const src = originals.get(n) ?? n.nodeValue;
          if (!originals.has(n)) originals.set(n, src);
          const key = `${lang}|${src.trim()}`;
          if (memo[key]) { if (n.nodeValue !== memo[key]) n.nodeValue = memo[key]; }
          else pending.push(src.trim());
        });
        const uniq = [...new Set(pending)];
        for (let i = 0; i < uniq.length && !cancelled; i += 60) {
          const chunk = uniq.slice(i, i + 60);
          const res = await fetch(`${BACKEND}/api/translate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lang, texts: chunk }) });
          if (!res.ok) break;
          const d = await res.json();
          chunk.forEach((t, j) => { memo[`${lang}|${t}`] = d.texts[j]; });
          collect(root).forEach((n) => { const src = (originals.get(n) ?? n.nodeValue).trim(); const t = memo[`${lang}|${src}`]; if (t && n.nodeValue.trim() !== t) { if (!originals.has(n)) originals.set(n, n.nodeValue); n.nodeValue = t; } });
        }
      } finally { busy.current = false; }
    };
    const t0 = setTimeout(run, 150);
    let timer;
    const obs = new MutationObserver(() => { if (busy.current) return; clearTimeout(timer); timer = setTimeout(run, 700); });
    obs.observe(root, { childList: true, subtree: true, characterData: true });
    return () => { cancelled = true; clearTimeout(t0); clearTimeout(timer); obs.disconnect(); };
  }, [contentLang, pathname]);

  return null;
};

export default PageTranslator;
